import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import classNames from 'classnames';

import lang from '../../lib/language';
import { CountryData } from '../../lib/models/enroll';
import CountriesCache from '../../lib/store-util/CountriesCache';

import ArrowBack from '../../assets/img/blue_icon-arrow-left.svg';
import searchAreaIcon from '../../assets/img/search-area.svg';
import { MobileCountryCodeSelector } from '../../containers/Verification/Verification.constants';
import { UI_ROUTES } from '../../util/routes';
import Country from '../Country/Country';
import { ICountryControlOptions } from '../CountryControl/CountryControl.types';
import { ClickableDiv, ClickableImage } from '../index';
import SearchMob from '../SearchSymbolsMob/Search/SearchMob';

import styles from './SearchCountryCodeMob.module.scss';

const hasLetters = (value: string): boolean => /[a-zA-Z]/.test(value);

const normalize = (value: string): string => value.toLowerCase().trim();

const filterCountryOptions = (
  options: ICountryControlOptions[],
  searchValue: string,
): ICountryControlOptions[] => {
  const query = normalize(searchValue);

  if (!query) {
    return options;
  }

  if (hasLetters(query)) {
    return options.filter((option) => normalize(option.label).startsWith(query));
  }

  return options.filter((option) => normalize(String(option.value)).includes(query));
};

const SearchCountryCodeMob = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    [MobileCountryCodeSelector]?: ICountryControlOptions;
  } | null;
  const [searchedText, setSearchedText] = useState<string>('');
  const [filteredOptions, setFilteredOptions] = useState<ICountryControlOptions[]>([]);
  const [pressedOptionId, setPressedOptionId] = useState<string>('');

  const countries = CountriesCache.use();

  const onClickBack = () => {
    navigate(-1);
  };

  const options = useMemo((): ICountryControlOptions[] => {
    if (countries && countries.length > 0) {
      return countries
        .filter((country: CountryData) => country.id && country.name && country.phone_code)
        .sort((current: CountryData, next: CountryData) => {
          if (current.name.toLowerCase() < next.name.toLowerCase()) {
            return -1;
          }
          if (current.name.toLowerCase() > next.name.toLowerCase()) {
            return 1;
          }
          return 0;
        })
        .map((country: CountryData) => ({
          label: country.name,
          value: `${country.phone_code} ${country.name}`,
          countryId: country.id,
        }));
    }

    return [];
  }, [countries]);

  const handleCountryCodeClick = (selectedCountryCode: ICountryControlOptions) => {
    navigate(UI_ROUTES.enrollPhoneVerification, {
      state: {
        [MobileCountryCodeSelector]: selectedCountryCode,
      },
      replace: true,
    });
  };

  const selectedCountryCode = locationState?.[MobileCountryCodeSelector];

  const getOptionId = (option: ICountryControlOptions): string =>
    option.countryId || option.value.toString();

  const isSelectedOption = (option: ICountryControlOptions): boolean => {
    if (!selectedCountryCode) {
      return false;
    }

    if (selectedCountryCode.countryId && option.countryId) {
      return selectedCountryCode.countryId === option.countryId;
    }

    return selectedCountryCode.value.toString() === option.value.toString();
  };

  useEffect(() => {
    setFilteredOptions(filterCountryOptions(options, searchedText));
  }, [options, searchedText]);

  return (
    <div id="SearchCountryCode" className={styles.wrapper}>
      <div className={styles.searchWrapper}>
        <ClickableImage className={styles.arrowBack} src={ArrowBack} onClick={onClickBack} />
        <SearchMob searchStocks={(val) => setSearchedText(val)} />
      </div>
      <div className={styles.options}>
        {filteredOptions.length === 0 ? (
          <div className={styles.noresults}>
            <img src={searchAreaIcon} alt="Search Area Icon" />
            <p>{lang.commonVerificationCountryCodeNoResults(searchedText)}</p>
          </div>
        ) : (
          filteredOptions.map((option) => {
            const optionId = getOptionId(option);
            const isSelected = isSelectedOption(option);
            const isPressed = pressedOptionId === optionId;

            return (
              <ClickableDiv
                key={optionId}
                className={classNames(styles.button, {
                  [styles['button-selected']]: isSelected || isPressed,
                })}
                onTouchStart={() => setPressedOptionId(optionId)}
                onTouchEnd={() => setPressedOptionId('')}
                onTouchCancel={() => setPressedOptionId('')}
                onClick={() => handleCountryCodeClick(option)}
              >
                <Country
                  id={option.countryId ?? (option.value as string)}
                  phoneCode={option.value as string}
                />
              </ClickableDiv>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SearchCountryCodeMob;
